import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  FileText, Plus, Upload, Wand2, Edit, Trash2, Eye, Copy, Share2, 
  Loader2, CheckCircle, Lock, Users, Building, Search, Filter,
  FileUp, PenTool, Sparkles, Save, X, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Template variable placeholders
const TEMPLATE_VARIABLES = [
  { key: '{{resource_name}}', label: 'Resource Name', category: 'Resource' },
  { key: '{{resource_role}}', label: 'Resource Role', category: 'Resource' },
  { key: '{{daily_rate}}', label: 'Daily Rate', category: 'Resource' },
  { key: '{{pm_company_name}}', label: 'PM Company Name', category: 'Company' },
  { key: '{{rm_company_name}}', label: 'RM Company Name', category: 'Company' },
  { key: '{{pm_company_address}}', label: 'PM Company Address', category: 'Company' },
  { key: '{{rm_company_address}}', label: 'RM Company Address', category: 'Company' },
  { key: '{{project_title}}', label: 'Project Title', category: 'Project' },
  { key: '{{project_description}}', label: 'Project Description', category: 'Project' },
  { key: '{{start_date}}', label: 'Start Date', category: 'Dates' },
  { key: '{{end_date}}', label: 'End Date', category: 'Dates' },
  { key: '{{duration_months}}', label: 'Duration (months)', category: 'Dates' },
  { key: '{{today_date}}', label: "Today's Date", category: 'Dates' },
  { key: '{{payment_terms}}', label: 'Payment Terms', category: 'Financial' },
  { key: '{{total_amount}}', label: 'Total Amount', category: 'Financial' },
];

const MyContractTemplatesPage = () => {
  const navigate = useNavigate();
  const { user, company } = useContext(AuthContext);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createMode, setCreateMode] = useState('manual'); // manual, upload, ai
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all, personal, company, snow
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    type: 'time_material',
    description: '',
    content: '',
    visibility: 'personal',
    aiPrompt: ''
  });
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadParsing, setUploadParsing] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const response = await axios.get(`${API}/contract-templates/my-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates || response.data || []);
    } catch (error) {
      // Fallback to regular templates endpoint
      try {
        const response = await axios.get(`${API}/contract-templates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTemplates(response.data || []);
      } catch (e) {
        console.error('Failed to load templates:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF, Word document, or text file');
      return;
    }

    setUploadFile(file);
    setUploadParsing(true);

    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(
        `${API}/contract-templates/parse-document`,
        formDataUpload,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Set extracted content
      setFormData(prev => ({
        ...prev,
        name: file.name.replace(/\.[^/.]+$/, ''),
        content: response.data.content || response.data.text || '',
        description: `Imported from ${file.name}`
      }));

      toast.success('Document content extracted successfully!');
    } catch (error) {
      toast.error('Failed to parse document. Please try manual entry.');
      console.error(error);
    } finally {
      setUploadParsing(false);
    }
  };

  const handleAIGenerate = async () => {
    if (!formData.aiPrompt.trim()) {
      toast.error('Please describe what contract you want to generate');
      return;
    }

    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/contract-templates/generate-ai`,
        {
          prompt: formData.aiPrompt,
          type: formData.type,
          language: 'fr' // French by default
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setFormData(prev => ({
        ...prev,
        content: response.data.content,
        name: response.data.suggested_name || prev.name,
        description: response.data.description || prev.description
      }));

      toast.success('Contract template generated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate template');
      console.error(error);
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    if (!formData.content.trim()) {
      toast.error('Template content cannot be empty');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/contract-templates/create`,
        {
          name: formData.name,
          type: formData.type,
          description: formData.description,
          content_template: formData.content,
          visibility: formData.visibility,
          created_by_company: company?.id
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Template saved successfully!');
      setShowCreateModal(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/contract-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'time_material',
      description: '',
      content: '',
      visibility: 'personal',
      aiPrompt: ''
    });
    setUploadFile(null);
    setCreateMode('manual');
  };

  const insertVariable = (variable) => {
    setFormData(prev => ({
      ...prev,
      content: prev.content + ' ' + variable
    }));
  };

  const filteredTemplates = templates.filter(t => {
    const matchesSearch = !search || 
      t.name?.toLowerCase().includes(search.toLowerCase()) ||
      t.description?.toLowerCase().includes(search.toLowerCase());
    
    const matchesFilter = filter === 'all' || 
      (filter === 'personal' && t.visibility === 'personal') ||
      (filter === 'company' && t.visibility === 'company') ||
      (filter === 'snow' && t.is_predefined);
    
    return matchesSearch && matchesFilter;
  });

  const getVisibilityBadge = (template) => {
    if (template.is_predefined) {
      return <Badge className="bg-blue-100 text-blue-800">Snow Exchange</Badge>;
    }
    if (template.visibility === 'company') {
      return <Badge className="bg-purple-100 text-purple-800"><Building className="w-3 h-3 mr-1" />Company</Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-800"><Lock className="w-3 h-3 mr-1" />Personal</Badge>;
  };

  const getTypeBadge = (type) => {
    const types = {
      'nda': { label: 'NDA', color: 'bg-orange-100 text-orange-800' },
      'non_compete': { label: 'Non-Compete', color: 'bg-red-100 text-red-800' },
      'time_material': { label: 'Time & Material', color: 'bg-green-100 text-green-800' },
      'regie': { label: 'Time & Material', color: 'bg-green-100 text-green-800' },
      'fixed_price': { label: 'Fixed Price', color: 'bg-blue-100 text-blue-800' },
      'project_based': { label: 'Fixed Price', color: 'bg-blue-100 text-blue-800' },
      'freelance': { label: 'Freelance', color: 'bg-purple-100 text-purple-800' }
    };
    const t = types[type] || { label: type, color: 'bg-gray-100 text-gray-800' };
    return <Badge className={t.color}>{t.label}</Badge>;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#1B3240' }}>My Contract Templates</h1>
          <p className="text-gray-600 mt-1">Create, manage, and share contract templates</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} style={{ backgroundColor: '#62D84E' }}>
          <Plus className="w-4 h-4 mr-2" />
          Create Template
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search templates..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[180px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Templates</SelectItem>
                <SelectItem value="personal">My Templates</SelectItem>
                <SelectItem value="company">Company Templates</SelectItem>
                <SelectItem value="snow">Snow Exchange</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Templates Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin" />
        </div>
      ) : filteredTemplates.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No templates found</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => setShowCreateModal(true)}
            >
              Create your first template
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <Card key={template.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="text-lg line-clamp-1">{template.name}</CardTitle>
                    <CardDescription className="line-clamp-2 mt-1">
                      {template.description || 'No description'}
                    </CardDescription>
                  </div>
                  <FileText className="w-8 h-8 text-gray-300 ml-2" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-4 flex-wrap">
                  {getTypeBadge(template.type)}
                  {getVisibilityBadge(template)}
                </div>
                
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(`/contract-templates/${template.id}/view`)}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View
                  </Button>
                  {!template.is_predefined && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleDeleteTemplate(template.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Templates Disclaimer */}
      <p className="text-xs text-gray-500 mt-6 text-center">
        Templates are provided for convenience only and must be independently reviewed.
      </p>

      {/* Create Template Modal */}
      <Dialog open={showCreateModal} onOpenChange={(open) => {
        if (!open) resetForm();
        setShowCreateModal(open);
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Contract Template</DialogTitle>
            <DialogDescription>
              Choose how you want to create your template
            </DialogDescription>
          </DialogHeader>

          {/* Creation Mode Tabs */}
          <Tabs value={createMode} onValueChange={setCreateMode} className="mt-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="manual" className="flex items-center gap-2">
                <PenTool className="w-4 h-4" />
                Manual
              </TabsTrigger>
              <TabsTrigger value="upload" className="flex items-center gap-2">
                <FileUp className="w-4 h-4" />
                Import File
              </TabsTrigger>
              <TabsTrigger value="ai" className="flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                AI Generate
              </TabsTrigger>
            </TabsList>

            {/* Upload Tab */}
            <TabsContent value="upload" className="mt-4">
              <div 
                className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadParsing ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="w-10 h-10 animate-spin text-primary mb-2" />
                    <p className="text-sm text-gray-600">Parsing document...</p>
                  </div>
                ) : uploadFile ? (
                  <div className="flex flex-col items-center">
                    <CheckCircle className="w-10 h-10 text-green-500 mb-2" />
                    <p className="font-medium">{uploadFile.name}</p>
                    <p className="text-sm text-gray-500 mt-1">Content extracted. Edit below if needed.</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <Upload className="w-10 h-10 text-gray-400 mb-2" />
                    <p className="font-medium">Click to upload a document</p>
                    <p className="text-sm text-gray-500 mt-1">Supported formats: PDF, Word (.docx), Text (.txt)</p>
                  </div>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={handleFileUpload}
                />
              </div>
            </TabsContent>

            {/* AI Generation Tab */}
            <TabsContent value="ai" className="mt-4 space-y-4">
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg border border-purple-200">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-6 h-6 text-purple-500 mt-1" />
                  <div>
                    <h4 className="font-medium text-purple-900">AI Contract Generator</h4>
                    <p className="text-sm text-purple-700 mt-1">
                      Describe the contract you need in your own words. Our AI will generate a professional template based on your requirements.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <Label>Contract Type</Label>
                <Select 
                  value={formData.type} 
                  onValueChange={(v) => setFormData({...formData, type: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nda">NDA (Non-Disclosure Agreement)</SelectItem>
                    <SelectItem value="non_compete">Non-Compete Agreement</SelectItem>
                    <SelectItem value="time_material">Time & Material Contract</SelectItem>
                    <SelectItem value="fixed_price">Fixed Price Contract</SelectItem>
                    <SelectItem value="freelance">Freelance Contract</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Describe your contract requirements</Label>
                <Textarea
                  className="mt-1 min-h-[150px]"
                  placeholder="Example: Je souhaite un contrat de prestation de services en régie pour une mission ServiceNow, avec une clause de confidentialité renforcée, des conditions de paiement à 30 jours, et une période d'essai de 1 mois..."
                  value={formData.aiPrompt}
                  onChange={(e) => setFormData({...formData, aiPrompt: e.target.value})}
                />
              </div>

              <Button 
                onClick={handleAIGenerate} 
                disabled={generating || !formData.aiPrompt.trim()}
                className="w-full"
                style={{ backgroundColor: '#62D84E' }}
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4 mr-2" />
                    Generate Contract
                  </>
                )}
              </Button>
            </TabsContent>

            {/* Manual Tab - Always shows the form */}
            <TabsContent value="manual" className="mt-4">
              <p className="text-sm text-gray-500 mb-4">
                Create your template by filling in the form below. Use variables to auto-fill data.
              </p>
            </TabsContent>
          </Tabs>

          {/* Common Form Fields (shown for all modes after content is available) */}
          <div className="space-y-4 mt-6 border-t pt-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Template Name *</Label>
                <Input
                  className="mt-1"
                  placeholder="e.g., NDA Standard"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                />
              </div>
              <div>
                <Label>Contract Type</Label>
                <Select 
                  value={formData.type} 
                  onValueChange={(v) => setFormData({...formData, type: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nda">NDA</SelectItem>
                    <SelectItem value="non_compete">Non-Compete</SelectItem>
                    <SelectItem value="time_material">Time & Material</SelectItem>
                    <SelectItem value="fixed_price">Fixed Price</SelectItem>
                    <SelectItem value="freelance">Freelance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Input
                className="mt-1"
                placeholder="Brief description of this template"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
              />
            </div>

            <div>
              <Label>Visibility</Label>
              <Select 
                value={formData.visibility} 
                onValueChange={(v) => setFormData({...formData, visibility: v})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal">
                    <div className="flex items-center">
                      <Lock className="w-4 h-4 mr-2" />
                      Personal (Only you)
                    </div>
                  </SelectItem>
                  <SelectItem value="company">
                    <div className="flex items-center">
                      <Building className="w-4 h-4 mr-2" />
                      Company (All team members)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Template Variables */}
            <div>
              <Label className="flex items-center gap-2">
                Available Variables
                <span className="text-xs text-gray-500">(click to insert)</span>
              </Label>
              <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                <div className="flex flex-wrap gap-2">
                  {TEMPLATE_VARIABLES.map((v) => (
                    <button
                      key={v.key}
                      type="button"
                      className="px-2 py-1 text-xs bg-white border rounded hover:bg-blue-50 hover:border-blue-300 transition-colors"
                      onClick={() => insertVariable(v.key)}
                      title={v.label}
                    >
                      {v.key}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Content Editor */}
            <div>
              <Label>Template Content *</Label>
              <Textarea
                className="mt-1 min-h-[300px] font-mono text-sm"
                placeholder="Enter your contract content here. Use {{variable}} syntax for dynamic fields..."
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
              />
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveTemplate} 
              disabled={saving || !formData.name || !formData.content}
              style={{ backgroundColor: '#62D84E' }}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Template
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyContractTemplatesPage;
